import React from "react";
import type { Building } from "../../../../api/client/types/building.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import { PaginatedTable } from "../../../../shared/components/ui/PaginatedTable";
import { StatusBadge } from "../../../../shared/components/ui/StatusBadge";
import { EditButton } from "../../../../shared/components/ui/EditButton";
import { type ColumnDef } from "@tanstack/react-table";
import { TRANSLATIONS } from "../../../../shared/utils/translations";
import { pluralize } from "../../../../shared/utils/stringUtils";

interface BuildingTableProps {
  buildings: PaginatedResponse<Building>;
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  /** Callback when the user clicks the edit icon */
  onEditBuilding?: (building: Building) => void;

  /** Optional count of active buildings to display instead of total */
  activeBuildingsCount?: number;
}

export const BuildingTable: React.FC<BuildingTableProps> = ({
  buildings,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEditBuilding,
  activeBuildingsCount,
}) => {
  const columns = React.useMemo<ColumnDef<Building>[]>(
    () => [
      {
        id: "actions",
        header: "Acciones",
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <EditButton
              onClick={() => onEditBuilding?.(row.original)}
              title="Editar unidad residencial"
              iconSize={16}
            />
            <button
              type="button"
              onClick={() => onEditBuilding?.(row.original)}
              className="text-xs leading-none text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
              title="Editar unidad residencial"
            >
            </button>
          </div>
        ),
        enableSorting: false,
      },
      { header: "Nombre", accessorKey: "name" },
      {
        header: "Ciudad",
        accessorKey: "city.name",
        cell: ({ row }) => row.original.city?.name || "—",
      },
      {
        header: "Estado",
        accessorKey: "status.name",
        cell: ({ row }) => {
          const statusCode = row.original.status.name;
          const translatedStatus =
            TRANSLATIONS.status[
              statusCode as keyof typeof TRANSLATIONS.status
            ] || statusCode;
          return (
            <StatusBadge statusCode={statusCode} label={translatedStatus} />
          );
        },
      },
      {
        header: "Creado el",
        accessorKey: "created_at",
        cell: ({ getValue }) =>
          new Date(getValue() as string).toLocaleDateString("es-CO"),
      },
    ],
    [onEditBuilding]
  );

  return (
    <PaginatedTable<Building>
      data={buildings}
      columns={columns}
      isLoading={isLoading}
      isError={isError}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      totalLabel="Total"
      entityName={activeBuildingsCount ? 
        pluralize(activeBuildingsCount, "unidad residencial activa", "unidades residenciales activas") 
        : pluralize(buildings?.total ?? 0, "unidad residencial", "unidades residenciales")}
      customTotal={activeBuildingsCount}
    />
  );
};

